import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Calendar,
  DatePicker,
  Grid,
  Select,
  Space,
  Spin,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  HomeOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import Layout from "../../Components/Layout";
import { apiFetch } from "../../services/apiClient";
import { useAuth } from "../../context/AuthContext";
import BookingDetailModal from "./bookingDetailModal";
import { type CreatePayload } from "./calendarCreateModal";
import ClientBlockModal, { type ClientBlockInitial } from "./ClientBlockModal";
import { isAdminRole } from "../../utils/roles.utils";
import { formatEstadiaCode } from "../../utils/estadiaCode";
import {
  useCalendarioLogic,
  type CalendarCellData,
  type CalendarRow,
  type CellThirdKey,
  type CellState,
  type CalendarEventRef,
  type Property,
} from "./useCalendarioLogic";
import "./calendario.css";
import { ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import {
  ISO_FMT,
  THIRD_FRAC,
  type DaySpan,
  startFracFromMinutes,
  endFracFromMinutes,
  minutesFromHHmm,
  isoDate,
  makeBookingKey,
  bookingLabel,
  buildWeekLanes,
  dateFromISO,
  dedupeEventRefs,
  isMergedEmpty,
} from "./calendarioClienteMonth.helpers";
import CalendarDayCell from "./components/CalendarDayCell";
import { formatPropertyName } from "../../utils/propertyDisplay";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../utils/antdStatic";
import { ahoraChile } from "../../utils/fechaChile";

export { dedupeEventRefs };





export default function CalendarioClienteMonth() {
  const { user } = useAuth();

  const {
    loading,
    refreshing,
    error,
    properties,
    dates,
    getCell,
    sidebarOpen,
    toggleSidebar,
    calendarRows,
    blocks,
    refetch,
    refreshCalendar,
    fetchEventDetail,
    range,
    setRange,
    nightPrices,
    fetchPropertyMeta,
    focusedPropertyId,
    setFocusedPropertyId,
  } = useCalendarioLogic();

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [month, setMonth] = useState<Dayjs>(() => {
    // Restaurar el último mes visto (persistido en localStorage) para que al
    // RECARGAR la página el cliente siga en el mismo mes, no en el actual
    // (pedido del owner 2026-07-03).
    try {
      const saved = localStorage.getItem("af.calendarioCliente.mes");
      if (saved) {
        const d = dayjs(saved);
        if (d.isValid()) return d.startOf("month");
      }
    } catch {
      /* localStorage no disponible (modo privado) → default */
    }
    return ahoraChile().startOf("month");
  });
  // La propiedad visible del cliente se deriva del hook (que ya auto-selecciona
  // la primera —o la última vista, restaurada de localStorage— al llegar las
  // propiedades). Así no hay doble render: vacío → con datos.
  const selectedPropertyId = focusedPropertyId;
  const setSelectedPropertyId = setFocusedPropertyId;

  // Carga LAZY de precios/disponibilidad de la propiedad enfocada. El request
  // principal del calendario YA NO trae los precios (para no bloquear el primer
  // paint con el feed externo de Stays, ~90% de la latencia — ver
  // useCalendarioLogic + stays.controller.js). Los pedimos por separado, sin
  // bloquear: los eventos (arriendos/bloqueos/aseos) se pintan al instante y el
  // precio por noche aparece progresivamente. `fetchPropertyMeta` cachea por
  // propiedad y se rehace al cambiar de mes (depende del rango), así que este
  // effect re-pide al navegar meses sin duplicar llamadas dentro del mismo mes.
  useEffect(() => {
    if (selectedPropertyId != null) {
      void fetchPropertyMeta(selectedPropertyId);
    }
  }, [selectedPropertyId, fetchPropertyMeta]);

  // Persistir mes + propiedad seleccionados para conservarlos al recargar.
  // La RESTAURACIÓN de la propiedad vive en useCalendarioLogic (auto-select)
  // para evitar un flash a la propiedad por defecto; acá solo escribimos.
  useEffect(() => {
    try {
      localStorage.setItem(
        "af.calendarioCliente.mes",
        month.startOf("month").format("YYYY-MM-DD"),
      );
    } catch {
      /* noop */
    }
  }, [month]);

  useEffect(() => {
    if (selectedPropertyId == null) return;
    try {
      localStorage.setItem(
        "af.calendarioCliente.propiedadId",
        String(selectedPropertyId),
      );
    } catch {
      /* noop */
    }
  }, [selectedPropertyId]);

  const [selectedCell, setSelectedCell] = useState<CalendarCellData | null>(
    null,
  );
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Estado del modal simplificado cliente (bloqueo / mantención)
  const [isBlockOpen, setIsBlockOpen] = useState(false);
  const [blockMode, setBlockMode] = useState<"create" | "edit">("create");
  const [blockInitial, setBlockInitial] = useState<ClientBlockInitial | null>(
    null,
  );
  const [blockEditingKind, setBlockEditingKind] = useState<
    "block" | "maintenance"
  >("block");

  // El listener de `CALENDAR_CHANGED` vive ahora en useCalendarioLogic.ts
  // (hook común para admin y cliente, con debounce). No duplicar aquí.

  // Auto-selección de propiedad inicial ya se hace dentro de useCalendarioLogic
  // para evitar el flash "sin datos → con datos" al recargar la página.

  // range por mes (incluye semanas completas para que el calendario no quede sin datos en días de borde)
  useEffect(() => {
    const fromD = month.startOf("month").startOf("week");
    const toD = month.endOf("month").endOf("week");

    const from = fromD.format(ISO_FMT);
    const to = toD.format(ISO_FMT);

    if (range.from !== from || range.to !== to) {
      setRange({ from, to });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  /**
   * Filas de la propiedad elegida, SIN los aseos.
   *
   * Round 2026-07-28 (pedido del dueño): el cliente no ve aseos en su
   * calendario. Antes veía una barra "Aseo" por cada limpieza de su propiedad
   * —sin monto ni detalle, pero visible— y eso convertía su calendario en una
   * agenda operativa de Aloja Fácil en vez de un calendario de disponibilidad.
   *
   * Se filtra ACÁ, en la única fuente de la que cuelga todo lo demás, en vez
   * de en cada consumidor: las barras (`cleaningSpans`), el pintado de la
   * celda y el modal de detalle salen todos de estas filas. Hay que limpiar
   * las DOS formas en que viaja un evento —la lista plana `events` y las
   * celdas ya calculadas `cellsByDate`— porque cada consumidor usa una.
   *
   * El día sigue protegido: crear un bloqueo sobre un aseo lo rechaza el
   * backend (`checkCalendarOverlap`), que es la validación que manda. Lo que
   * se pierde es el aviso anticipado en pantalla — a cambio de que el cliente
   * no vea la operación interna, que es lo que se pidió.
   */
  const rowsOfSelectedProperty = useMemo(() => {
    if (!selectedPropertyId) return [];
    const sinAseos = (ev: any) => ev?.kind !== "cleaning";
    return calendarRows
      .filter((r) => r.propertyId === selectedPropertyId)
      .map((r) => {
        const cellsByDate: typeof r.cellsByDate = {};
        for (const [fecha, cell] of Object.entries(r.cellsByDate ?? {})) {
          const thirds = { ...cell.thirds };
          for (const k of Object.keys(thirds) as (keyof typeof thirds)[]) {
            const t = thirds[k];
            const events = (t.events ?? []).filter(sinAseos);
            thirds[k] = {
              ...t,
              events,
              // Si el tercio estaba pintado como aseo y ya no queda ningún
              // evento, vuelve a verse libre. Sin esto quedaría un tercio
              // coloreado sin nada detrás.
              state: t.state === "cleaning" && !events.length ? "empty" : t.state,
            };
          }
          cellsByDate[fecha] = { ...cell, thirds };
        }
        return {
          ...r,
          events: (r.events ?? []).filter(sinAseos),
          cellsByDate,
        };
      });
  }, [calendarRows, selectedPropertyId]);

  // Calendario usa siempre el celeste uniforme — el color por propiedad
  // (`property.color`) es para los chips de /tareas, no para el calendario.
  // Antes leíamos p?.color, lo cual coloreaba cada propiedad distinto tras
  // el backfill del commit bca6f5a; revertimos al comportamiento previo.
  const selectedPropertyColor = useMemo(() => {
    return "#2EC5F5";
  }, []);

  // Arriendos (dedupe) de la propiedad seleccionada.
  // Convención: check-in 15:00 en `ingreso`, check-out 11:00 en `salida`.
  // Por tanto la barra arranca en el tercio `evening` (2/3) del día de
  // ingreso y termina en el corte `morning` (1/3) del día de salida —
  // exactamente igual que en la vista admin.
  const bookingSpans = useMemo<DaySpan[]>(() => {
    const map = new Map<string, DaySpan>();
    for (const r of rowsOfSelectedProperty) {
      for (const b of (r as any).bookings ?? []) {
        if (!b?.ingreso || !b?.salida) continue;
        const key = makeBookingKey(b);
        if (!map.has(key)) {
          const ingresoDay = String(b.ingreso).slice(0, 10);
          const salidaDay = String(b.salida).slice(0, 10);
          // El cliente ve el Código de estadía AF al pasar el mouse por encima,
          // para poder identificar el arriendo (ej. al reclamar). Es un id
          // neutro, no sensible — mismo #N que ya ve en /finanzas-cliente.
          const codigo = b.id ?? b.bookingDbId ?? null;
          map.set(key, {
            key: `book:${key}`,
            kind: "booking",
            barStartISO: ingresoDay,
            barEndISO: salidaDay,
            startFrac: THIRD_FRAC.eveningStart, // 15:00
            endFrac: THIRD_FRAC.morningEnd, // 11:00
            label: bookingLabel(b),
            tooltip: (
              <span>
                {codigo != null && (
                  <>
                    <strong>Estadía {formatEstadiaCode(codigo)}</strong>
                    <br />
                  </>
                )}
                {bookingLabel(b)} — {ingresoDay} → {salidaDay}
              </span>
            ),
            sourceId: codigo ?? undefined,
          });
        }
      }
    }
    return Array.from(map.values());
  }, [rowsOfSelectedProperty]);

  // Bloqueos / mantenciones de la propiedad seleccionada, como spans continuos.
  // Se marcan como "propio" los que el usuario actual creó (coincide
  // `idUsuarioCreador`) y que no fueron hechos por admin.
  const currentUserId = (user as any)?.id ?? null;
  const blockSpans = useMemo<DaySpan[]>(() => {
    if (!selectedPropertyId) return [];
    const out: DaySpan[] = [];
    for (const b of blocks) {
      if (b.idPropiedad !== selectedPropertyId) continue;
      if (!b.fechaInicio) continue;
      // fechaTermino es el día del "checkout" (tal cual lo persiste el
      // backend). Si no viene, asumimos el mismo día de inicio.
      const fechaTermino = b.fechaTermino ?? b.fechaInicio;
      const kind: DaySpan["kind"] =
        b.tipo === "maintenance" ? "maintenance" : "block";
      const isMaintenance = kind === "maintenance";
      const baseLabel = isMaintenance ? "Mantención" : "Bloqueo";
      // Color se basa en el CREADOR del registro (admin vs cliente),
      // no en si el cliente logueado es o no el dueño. Regla canónica
      // (modulo-calendario.md §4): `.is-from-client` SOLO cuando
      // `createdByAdmin === false` — el flag es TRI-estado y `null` es
      // un bloqueo nacido de un webhook de Stays, que se pinta con el
      // color oscuro (admin/sistema) igual que en la vista admin. El
      // `!== true` viejo pintaba los de Stays con el color claro del
      // cliente → el mismo bloqueo se veía distinto en cada vista
      // (fix 2026-07-02).
      const isFromClient = b.createdByAdmin === false;
      const isFromStays = b.createdByAdmin == null;
      // "Tuyo" ↔ edición: sólo si el cliente logueado es el creador
      // real del bloqueo.
      const isOwn =
        isFromClient &&
        currentUserId != null &&
        b.idUsuarioCreador === currentUserId;
      const authorTag = isOwn
        ? "Tuyo"
        : isFromClient
          ? "Otro cliente"
          : isFromStays
            ? "Desde Stays"
            : "Admin";

      // Barra por tercios (ver docs/calendario-logica.md §2). Usamos las
      // horas persistidas en DB; si vienen vacías asumimos "día entero"
      // (00:00 → 23:59). Convención cliente: 15:00 → 11:00.
      const startMin = minutesFromHHmm(b.horaInicio, 0);
      const endMin = minutesFromHHmm(b.horaTermino, 23 * 60 + 59);
      let startFrac = startFracFromMinutes(startMin);
      let endFrac = endFracFromMinutes(endMin);
      let barStartISO = b.fechaInicio;
      let barEndISO = fechaTermino;

      // Si `endFrac === 0` (p.ej. horaTermino = 00:00) el barEndISO queda
      // con ancho 0 — retrocedemos un día y forzamos endFrac=1.
      if (endFrac <= 0 && barEndISO !== barStartISO) {
        barEndISO = dayjs(barEndISO).subtract(1, "day").format(ISO_FMT);
        endFrac = 1;
      }
      // Mismo día pero rangos invertidos (start > end) → normalizamos a
      // día entero defensivamente.
      if (barStartISO === barEndISO && startFrac >= endFrac) {
        startFrac = 0;
        endFrac = 1;
      }

      const lastNightISO =
        b.fechaTermino && b.fechaInicio
          ? dayjs(b.fechaTermino).subtract(1, "day").format(ISO_FMT)
          : b.fechaInicio;
      // El cliente SOLO ve `razon` (campo público). `notas` es una "nota
      // interna" de admin y NUNCA debe mostrarse en vistas del cliente — ni
      // en este tooltip, ni en el modal de edición (ver ClientBlockModal).
      // Ver docs/modulo-calendario.md §"Visibilidad de campos por rol".
      const tooltipNode = (
        <div className="cal-tooltip-content">
          <div className="cal-tooltip-title">
            {baseLabel}
            <span className="cal-tooltip-badge">{authorTag}</span>
          </div>
          <div className="cal-tooltip-row">
            <span>Fechas</span>
            <strong>
              {b.fechaInicio} → {lastNightISO}
            </strong>
          </div>
          <div className="cal-tooltip-row">
            <span>Razón</span>
            <strong>{b.razon || "—"}</strong>
          </div>
        </div>
      );
      out.push({
        key: `blk:${b.id}`,
        kind,
        barStartISO,
        barEndISO,
        startFrac,
        endFrac,
        label: b.razon ? `${baseLabel}: ${b.razon}` : baseLabel,
        tooltip: tooltipNode,
        sourceId: b.id,
        isFromClient,
        isOwn,
      });
    }
    return out;
  }, [blocks, selectedPropertyId, currentUserId]);

  // Aseos de la propiedad seleccionada, también como spans.
  // Antes se renderizaban como badges separadas, por lo que ocupaban una
  // "línea" extra aun cuando no solapaban con ningún arriendo/bloqueo. Ahora
  // participan del mismo sistema de lanes: si un aseo cae en el mid del día
  // en que un arriendo sale por la mañana (frac 0..1/3) y otro arriendo
  // entra por la tarde (frac 2/3..), los 3 eventos comparten el mismo
  // lane. Solo se separa en líneas distintas cuando hay solape real de
  // tercios — misma regla que la vista admin.
  //
  // Visibilidad (rol cliente): el label y el tooltip dicen SÓLO "Aseo".
  // No se muestran aseador, notas, urgencia, sueldo, ni ningún otro detalle
  // operativo. Ver docs/modulo-calendario.md §"Visibilidad de campos por rol".
  const cleaningSpans = useMemo<DaySpan[]>(() => {
    if (!selectedPropertyId) return [];
    const seen = new Set<number>();
    const out: DaySpan[] = [];
    for (const r of rowsOfSelectedProperty) {
      for (const ev of (r as any).events ?? []) {
        if (ev?.kind !== "cleaning") continue;
        if (seen.has(ev.id)) continue;
        seen.add(ev.id);

        const startStr = String(ev.startISO ?? "");
        const endStr = String(ev.endISO ?? "");
        if (!startStr || !endStr) continue;

        const barStartISO = startStr.slice(0, 10);
        let barEndISO = endStr.slice(0, 10);

        const startMin = (() => {
          const t = startStr.slice(11, 16);
          return minutesFromHHmm(t, 0);
        })();
        const endMin = (() => {
          const t = endStr.slice(11, 16);
          return minutesFromHHmm(t, 23 * 60 + 59);
        })();

        let startFrac = startFracFromMinutes(startMin);
        let endFrac = endFracFromMinutes(endMin);

        if (endFrac <= 0 && barEndISO !== barStartISO) {
          barEndISO = dayjs(barEndISO).subtract(1, "day").format(ISO_FMT);
          endFrac = 1;
        }
        if (barStartISO === barEndISO && startFrac >= endFrac) {
          startFrac = 0;
          endFrac = 1;
        }

        out.push({
          key: `clean:${ev.id}`,
          kind: "cleaning",
          barStartISO,
          barEndISO,
          startFrac,
          endFrac,
          label: "Aseo",
          tooltip: "Aseo",
          sourceId: ev.id,
        });
      }
    }
    return out;
  }, [rowsOfSelectedProperty, selectedPropertyId]);

  // Spans unificados que comparten el sistema de lanes (así no se pisan entre sí).
  const allSpans = useMemo<DaySpan[]>(
    () => [...bookingSpans, ...blockSpans, ...cleaningSpans],
    [bookingSpans, blockSpans, cleaningSpans],
  );

  // Cache de lanes por semana para soportar solapes — incluye arriendos +
  // bloqueos + mantenciones en el mismo stack (así nunca se pisan).
  const weekLaneCache = useMemo(() => {
    const from = dayjs(range.from);
    const to = dayjs(range.to);
    const map = new Map<string, ReturnType<typeof buildWeekLanes>>();

    let cursor = from.startOf("week");
    const end = to.endOf("week");
    while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
      map.set(isoDate(cursor), buildWeekLanes(cursor, allSpans));
      cursor = cursor.add(1, "week");
    }
    return map;
  }, [allSpans, range.from, range.to]);

  const mergeCellsForPropertyDay = (
    propertyId: number,
    date: string,
    rows: CalendarRow[],
  ): CalendarCellData => {
    const thirds: Record<
      CellThirdKey,
      { state: CellState; events?: CalendarEventRef[] }
    > = {
      morning: { state: "empty", events: [] },
      mid: { state: "empty", events: [] },
      evening: { state: "empty", events: [] },
    };

    const merged: CalendarCellData = {
      idPropiedad: propertyId,
      date,
      thirds,
      hasAny: false,
    };

    for (const row of rows) {
      const cell = getCell(row, date);
      merged.nombrePropiedad ??= cell.nombrePropiedad ?? row.propertyName;

      (Object.keys(cell.thirds) as CellThirdKey[]).forEach((k) => {
        const src = cell.thirds[k];
        const dst = merged.thirds[k];

        if (dst.state === "empty" && src.state !== "empty") {
          dst.state = src.state;
        }

        if (src.events?.length) {
          dst.events = [...(dst.events ?? []), ...src.events];
          merged.hasAny = true;
        }
      });
    }

    return merged;
  };

  const mergedByDate = useMemo(() => {
    if (!selectedPropertyId) return {};
    const map: Record<string, CalendarCellData> = {};
    for (const d of dates) {
      map[d] = mergeCellsForPropertyDay(
        selectedPropertyId,
        d,
        rowsOfSelectedProperty,
      );
    }
    return map;
  }, [dates, rowsOfSelectedProperty, selectedPropertyId]);

  // ¿El día `iso` tiene los 3 tercios ocupados por eventos AJENOS al bloqueo
  // en edición? Es el equivalente al `isDayFullyOccupied` del admin, para que
  // el RangePicker del cliente deshabilite las fechas donde NO queda ningún
  // tercio libre — pero NUNCA las del propio bloqueo (excludeBlockId), así el
  // cliente puede reencuadrar SU bloqueo sin poder encimarlo sobre otro evento
  // (otro bloqueo, arriendo, aseo). Compartir un día parcialmente (p.ej. con un
  // check-out en la mañana) sigue permitido: solo se deshabilita si los 3
  // tercios están tomados. La validación fina por tercio la hace igual
  // `validateNoOverlap` al guardar. Round 2026-07-03.
  const isDayFullyOccupiedForClient = useCallback(
    (iso: string, excludeBlockId?: string | number | null): boolean => {
      const merged = mergedByDate[iso];
      if (!merged) return false;
      const thirds = ["morning", "mid", "evening"] as const;
      return thirds.every((t) => {
        const events = merged.thirds[t].events ?? [];
        return events.some((ev) => {
          const evId =
            ev.kind === "booking"
              ? ((ev as any).bookingDbId ?? (ev as any).bookingId ?? null)
              : ev.kind === "cleaning"
                ? ((ev as any).cleaningId ?? null)
                : ((ev as any).id ?? null);
          // Solo se excluye el PROPIO bloqueo/mantención en edición. Exigimos
          // que el evento sea block/maintenance además del id, porque los ids
          // son PKs por tabla y un arriendo/aseo podría tener el mismo número
          // que el bloqueo — sin este guard se sub-excluiría por colisión.
          if (
            excludeBlockId != null &&
            (ev.kind === "blocked" || ev.kind === "maintenance") &&
            evId != null &&
            String(evId) === String(excludeBlockId)
          ) {
            return false;
          }
          return true;
        });
      });
    },
    [mergedByDate],
  );

  const canEditDelete = (ev: CalendarEventRef) => {
    // Cliente: solo puede editar/borrar bloqueos/mantenciones PROPIOS —
    // creado_por_admin=false Y id_usuario_creador === su user.id (regla de
    // modulo-calendario.md §2, misma que enforcea el backend). El `!== true`
    // viejo ofrecía editar bloqueos de OTRO cliente y los "Desde Stays"
    // (createdByAdmin null) y el backend después los rechazaba con error.
    if (isAdminRole((user as any)?.rol)) return true;
    if (ev.kind !== "blocked" && ev.kind !== "maintenance") return false;
    return (
      (ev as any).createdByAdmin === false &&
      currentUserId != null &&
      (ev as any).idUsuarioCreador === currentUserId
    );
  };

  const openDetailForDay = (d: Dayjs) => {
    if (!selectedPropertyId) return;
    const iso = d.format(ISO_FMT);
    const merged = mergedByDate[iso];
    if (!merged) return;

    setSelectedCell(merged);
    setIsDetailOpen(true);
  };

  /**
   * Valida que el rango cliente `[arrivalISO, departureISO]` (modelo
   * día-a-día, ver docs/calendario-logica.md §14) no pise ningún tercio
   * ya ocupado por otro evento de la misma propiedad.
   *
   * Tercios que ocupa el bloqueo cliente:
   *   - arrivalISO:                sólo `evening` (check-in 15:00)
   *   - cada día entre arrival y departure (exclusive): los 3 tercios
   *   - departureISO:              sólo `morning` (check-out 10:59)
   *
   * La validación es estricta: cualquier evento (arriendo, aseo, bloqueo,
   * mantención) que toque un tercio de esa lista hace fallar la creación
   * o edición del bloqueo cliente — da igual quién lo creó.
   *
   * En edit-mode ignoramos la colisión con el propio bloqueo
   * (`excludeBlockId`).
   */
  const validateNoOverlap = useCallback(
    (
      arrivalISO: string,
      departureISO: string,
      excludeBlockId?: string | number | null,
    ): string | null => {
      type Third = "morning" | "mid" | "evening";
      const tercioLabel: Record<Third, string> = {
        morning: "mañana",
        mid: "mediodía",
        evening: "tarde",
      };

      const eventIdOf = (ev: CalendarEventRef): string | number | null => {
        if (ev.kind === "booking") {
          return ev.bookingDbId ?? ev.bookingId ?? null;
        }
        if (ev.kind === "cleaning") return ev.cleaningId ?? null;
        return (ev as any).id ?? null;
      };

      const describeEvent = (ev: CalendarEventRef): string => {
        switch (ev.kind) {
          case "booking":
            return "un arriendo";
          case "cleaning":
            return "un aseo";
          case "maintenance":
            return "una mantención";
          default:
            return "un bloqueo";
        }
      };

      const checkTercio = (iso: string, tercio: Third): string | null => {
        const merged = mergedByDate[iso];
        if (!merged) return null;
        const events = merged.thirds[tercio].events ?? [];
        for (const ev of events) {
          const evId = eventIdOf(ev);
          // Excluir SOLO el propio bloqueo/mantención en edición (id + kind:
          // los ids son por tabla, un arriendo/aseo podría colisionar en id).
          if (
            excludeBlockId != null &&
            (ev.kind === "blocked" || ev.kind === "maintenance") &&
            evId != null &&
            String(evId) === String(excludeBlockId)
          ) {
            continue;
          }
          return `Se superpone con ${describeEvent(ev)} el ${iso} (${tercioLabel[tercio]}).`;
        }
        return null;
      };

      const arrival = dayjs(arrivalISO);
      const departure = dayjs(departureISO);
      if (
        !arrival.isValid() ||
        !departure.isValid() ||
        !departure.isAfter(arrival, "day")
      ) {
        return "El día de salida debe ser posterior al día de llegada.";
      }

      // Día de llegada: sólo `evening` (15:00 en adelante).
      const evConflict = checkTercio(arrivalISO, "evening");
      if (evConflict) return evConflict;

      // Días entre llegada y salida (exclusivos de ambos extremos): los
      // 3 tercios porque el bloqueo cubre el día completo.
      let cursor = arrival.add(1, "day");
      while (cursor.isBefore(departure, "day")) {
        const iso = cursor.format(ISO_FMT);
        for (const t of ["morning", "mid", "evening"] as Third[]) {
          const c = checkTercio(iso, t);
          if (c) return c;
        }
        cursor = cursor.add(1, "day");
      }

      // Día de salida: sólo `morning` (check-out 10:59).
      const morningConflict = checkTercio(departureISO, "morning");
      if (morningConflict) return morningConflict;

      return null;
    },
    [mergedByDate],
  );

  /**
   * ¿El día `iso` acepta iniciar un nuevo bloqueo cliente?
   *
   * El bloqueo mínimo es 1 noche → llegada `iso`, salida `iso + 1`. Por
   * tanto preguntamos si la combinación "evening de iso + morning de
   * iso+1" está libre. Si no, el día no acepta un nuevo bloqueo.
   *
   * Regla extra: no se pueden crear bloqueos en el pasado.
   */
  const canStartClientBlockOn = useCallback(
    (iso: string): boolean => {
      if (!selectedPropertyId) return false;
      const today = ahoraChile().startOf("day");
      if (dayjs(iso).isBefore(today, "day")) return false;
      const nextISO = dayjs(iso).add(1, "day").format(ISO_FMT);
      return validateNoOverlap(iso, nextISO, null) === null;
    },
    [selectedPropertyId, validateNoOverlap],
  );

  const openCreateBlock = (isoDate: string) => {
    if (!selectedPropertyId) return;
    setBlockMode("create");
    setBlockEditingKind("block");
    // Modelo día-a-día (ver docs/calendario-logica.md §14):
    //   startDate = día de llegada (15:00)
    //   endDate   = día de salida  (10:59 del día siguiente a la última noche)
    // Default: 1 noche mínima ⇒ endDate = startDate + 1.
    const departureISO = dayjs(isoDate).add(1, "day").format(ISO_FMT);
    setBlockInitial({
      kind: "block",
      startDate: isoDate,
      endDate: departureISO,
    });
    setIsBlockOpen(true);
  };

  const openCreateForDay = (isoDate: string) => {
    // Preservamos el nombre para el botón de cabecera; internamente ahora
    // abrimos el modal simplificado de cliente en lugar del modal admin.
    openCreateBlock(isoDate);
  };

  const onEditEvent = async (ev: CalendarEventRef, _slots: CellThirdKey[]) => {
    if (!selectedCell) return;
    if (!canEditDelete(ev)) return;

    const id = (ev as any).id;
    if (id == null) {
      getMsg()?.error("Este evento no tiene ID editable.");
      return;
    }

    // Clientes solo editan bloqueos / mantenciones — no aseos ni arriendos.
    if (ev.kind !== "blocked" && ev.kind !== "maintenance") {
      getMsg()?.info("Este evento no es editable desde aquí.");
      return;
    }

    const hide = getMsg()?.loading("Cargando detalle para editar…", 0);

    try {
      const kind = ev.kind === "blocked" ? "block" : ev.kind;
      const detail = await fetchEventDetail(kind, id);

      setBlockMode("edit");
      setBlockEditingKind(kind as "block" | "maintenance");
      setBlockInitial({
        id,
        kind: kind as "block" | "maintenance",
        startDate:
          detail.startDate ??
          dateFromISO((ev as any).start) ??
          selectedCell.date,
        endDate:
          detail.endDate ??
          dateFromISO((ev as any).end) ??
          selectedCell.date,
        razon: detail.razon ?? (ev as any).razon,
        notas: detail.notas ?? (ev as any).notas,
      });
      setIsBlockOpen(true);
      setIsDetailOpen(false);
    } catch (e: any) {
      console.error(e);
      getMsg()?.error(e?.message ?? "No pude cargar el detalle para editar");
    } finally {
      hide?.();
    }
  };

  const onDeleteEvent = async (ev: CalendarEventRef) => {
    if (!canEditDelete(ev)) return;

    const id = (ev as any).id;
    if (id == null) {
      getMsg()?.error("Este evento no tiene ID eliminable.");
      return;
    }
    const kind = ev.kind === "blocked" ? "block" : ev.kind;

    const path = `/stays/calendario?id=${encodeURIComponent(
      String(id),
    )}&kind=${encodeURIComponent(kind)}`;

    const res = await apiFetch<{ success?: boolean }>(path, {
      method: "DELETE",
      successMsg: "Eliminado",
    });
    if (!res) return;

    setIsDetailOpen(false);
    refetchKeepScroll();
  };

  async function deleteEvent(kind: string, id: number | string) {
    const path = `/stays/calendario?id=${encodeURIComponent(
      String(id),
    )}&kind=${encodeURIComponent(kind)}`;

    const json = await apiFetch<{ success: boolean; message?: string }>(path, {
      method: "DELETE",
    });
    if (!json?.success) throw new Error(json?.message ?? "Error borrando");

    refetchKeepScroll();
    return true;
  }

  /**
   * Wrapper de refetch que preserva el scroll de la ventana. Tras una
   * mutación (create / edit / delete) la data del Calendar cambia y
   * AntD re-renderiza — en algunos layouts esto dispara un scroll reset
   * cuando la altura del documento cambia (barras nuevas, layouts de
   * lanes, etc.). Guardamos la posición antes, refetcheamos, y la
   * restauramos en el próximo frame.
   */
  const refetchKeepScroll = async () => {
    const top =
      typeof window !== "undefined"
        ? window.scrollY || document.documentElement.scrollTop
        : 0;
    // Refresh SUAVE (preserveMeta), NO el duro `refetch()`. Una mutación de
    // bloqueo NO cambia precios, así que preservamos `nightPrices` ya cargados:
    // el refetch duro los borraba a {} y forzaba un round-trip lento a Stays
    // (getPropertyCalendarMeta), dejando todas las celdas SIN precio varios
    // segundos tras cada create/edit/delete (parpadeo). `includeMeta:false`
    // re-trae solo los eventos desde la DB, rápido y sin tocar la API externa.
    await Promise.resolve(refreshCalendar?.({ includeMeta: false }));
    if (typeof window !== "undefined") {
      requestAnimationFrame(() => {
        window.scrollTo({ top, behavior: "auto" });
      });
    }
  };

  /** Submit del ClientBlockModal (create + edit). */
  const onSubmitBlock = async (payload: CreatePayload) => {
    const isEdit = blockMode === "edit";
    try {
      const res = await apiFetch<{ success?: boolean }>("/stays/calendario", {
        method: isEdit ? "PATCH" : "POST",
        body: JSON.stringify(payload),
        successMsg: isEdit ? "Bloqueo actualizado" : "Bloqueo creado",
      });
      if (!res) return;
      setIsBlockOpen(false);
      setBlockInitial(null);
      setBlockMode("create");
      // Usamos el wrapper que preserva scroll + mes (el guard en
      // `onPanelChange` evita saltos de mes por re-render).
      refetchKeepScroll();
    } catch (e: any) {
      console.error("[onSubmitBlock] error", e);
      getMsg()?.error(e?.message ?? "Error guardando bloqueo");
    }
  };

  const onDeleteBlockFromModal = async () => {
    const id = blockInitial?.id;
    if (id == null) return;
    try {
      await deleteEvent(blockEditingKind, id);
      getMsg()?.success("Bloqueo eliminado ✅");
      setIsBlockOpen(false);
      setBlockInitial(null);
      setBlockMode("create");
    } catch (e: any) {
      getMsg()?.error(e?.message ?? "Error eliminando");
    }
  };

  // Sólo mostramos el spinner global en la carga inicial (sin propiedades
  // todavía). Las mutaciones usan `refreshing` y no tapan la UI.
  if (loading && properties.length === 0) {
    return (
      <div className="cal-page">
        <Spin />
      </div>
    );
  }

  if (error) {
    // Solo la carga INICIAL llega acá — los refreshes de fondo fallidos
    // conservan la vista y reintentan solos (ver catch de fetchCalendar).
    return (
      <div className="cal-page">
        <Alert
          type="error"
          message="Error cargando calendario"
          description={error}
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      </div>
    );
  }

  const propOptions = (properties ?? []).map((p: Property) => ({
    value: p.id,
    label: formatPropertyName(p.nombre),
  }));

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="lav-content"
    >
      <ConfigProvider locale={esES}>
        <div className="cal-client-wrap">
          <div className="cal-client-toolbar">
            <div className="left">
              <div className="cal-title">
                <CalendarOutlined style={{ marginRight: 8 }} />
                Calendario
              </div>
              <div className="cal-subtitle">
                Gestiona tus bloqueos y revisa reservas en tus propiedades.
              </div>
            </div>
            <div className="right">
              <Space
                wrap
                style={{
                  justifyContent: "flex-end",
                  width: isMobile ? "100%" : "auto",
                }}
                size={8}
              >
                <Tooltip title="Cambiar de propiedad">
                  <Select
                    style={{ minWidth: isMobile ? "100%" : 240 }}
                    placeholder="Selecciona una propiedad"
                    suffixIcon={<HomeOutlined />}
                    value={selectedPropertyId ?? undefined}
                    options={propOptions}
                    onChange={(v) => {
                      setSelectedPropertyId(v);
                      setFocusedPropertyId(v);
                    }}
                  />
                </Tooltip>

                <Space.Compact>
                  <Tooltip title="Mes anterior">
                    <Button
                      icon={<LeftOutlined />}
                      onClick={() =>
                        setMonth((m) => m.subtract(1, "month").startOf("month"))
                      }
                    />
                  </Tooltip>
                  <DatePicker
                    picker="month"
                    value={month}
                    allowClear={false}
                    onChange={(v) => v && setMonth(v.startOf("month"))}
                    format="MMMM YYYY"
                    style={{ width: isMobile ? 150 : 180 }}
                  />
                  <Tooltip title="Mes siguiente">
                    <Button
                      icon={<RightOutlined />}
                      onClick={() =>
                        setMonth((m) => m.add(1, "month").startOf("month"))
                      }
                    />
                  </Tooltip>
                  <Tooltip title="Ir al mes actual">
                    <Button
                      onClick={() => setMonth(ahoraChile().startOf("month"))}
                      disabled={month.isSame(ahoraChile(), "month")}
                    >
                      Hoy
                    </Button>
                  </Tooltip>
                </Space.Compact>

                <Tooltip
                  title={
                    !selectedPropertyId
                      ? "Primero selecciona una propiedad"
                      : "Crear un nuevo bloqueo o mantención (horario 15:00 → 10:59 del día siguiente)"
                  }
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!selectedPropertyId}
                    onClick={() => {
                      const d = ahoraChile().format(ISO_FMT);
                      openCreateForDay(d);
                    }}
                  >
                    Nuevo bloqueo
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

          {/*
           * Leyenda de colores — ayuda al cliente a interpretar rápido los
           * segmentos del calendario. Sólo se muestra cuando hay una
           * propiedad seleccionada (si no, la grilla está vacía igual).
           */}
          {selectedPropertyId ? (
            <div className="cal-legend">
              <span className="cal-legend-item">
                <span className="cal-legend-swatch is-kind-booking" />
                Arriendo
              </span>
              {/* La entrada "Aseo" se quitó en el round 2026-07-28: el
                  calendario del cliente ya no muestra aseos (ver el filtro de
                  `rowsOfSelectedProperty`), así que la leyenda anunciaba un
                  color que no aparece nunca. */}
              <span className="cal-legend-item">
                <span className="cal-legend-swatch is-kind-block is-from-client" />
                Bloqueo cliente
              </span>
              <span className="cal-legend-item">
                <span className="cal-legend-swatch is-kind-block" />
                Bloqueo externo
              </span>
              <span className="cal-legend-item">
                <span className="cal-legend-swatch is-kind-maintenance is-from-client" />
                Mantención cliente
              </span>
              <span className="cal-legend-item">
                <span className="cal-legend-swatch is-kind-maintenance" />
                Mantención externa
              </span>
              <span className="cal-legend-hint">
                Tip: clickea un día libre para crear un bloqueo.
              </span>
            </div>
          ) : (
            <Alert
              type="info"
              showIcon
              message="Selecciona una propiedad"
              description="Elige una propiedad del selector superior para ver su calendario."
              style={{ marginTop: 4, marginBottom: 8 }}
            />
          )}

          <div className="cal-month-wrap">
          {refreshing && (
            /*
             * Overlay que aparece mientras el calendario está recargando
             * (cambio de propiedad, edit/create/delete de bloqueo, etc.).
             * No reemplaza la UI: deja ver la data anterior semi-transparente
             * con un spinner centrado para indicar que está actualizando.
             */
            <div className="cal-refresh-overlay">
              <Spin size="large" />
            </div>
          )}
          <Calendar
            className="cal-month"
            fullscreen={false}
            value={month}
            /*
             * Ocultamos el header nativo de AntD (selects de mes/año + toggle
             * Mes/Año). La navegación principal vive en el toolbar superior,
             * junto al selector de propiedad — es una sola fuente, más limpia
             * y coherente en UI/UX.
             */
            headerRender={() => null}
            onSelect={(d, info) => {
              // Ant Design expone `info.source` ∈ "date" | "month" | "year" |
              // "customize". Sólo queremos reaccionar a la selección real de
              // un día en la grilla — los selects de mes/año del header del
              // calendario NO deben abrir el modal de creación.
              if (info?.source && info.source !== "date") {
                if (info.source === "month" || info.source === "year") {
                  setMonth(d.startOf("month"));
                }
                return;
              }
              // Click en día vacío futuro → crear bloqueo.
              // Click en día pasado vacío → ignorar (no se puede crear en pasado).
              // Click en día con arriendo/aseo/bloqueo → abrir detalle.
              const iso = d.format(ISO_FMT);
              const merged = mergedByDate[iso];
              const hasSpanOnDay = allSpans.some((s) => {
                const bStart = dayjs(s.barStartISO);
                const bLast = dayjs(s.barEndISO);
                return !d.isBefore(bStart, "day") && !d.isAfter(bLast, "day");
              });
              if (hasSpanOnDay || !isMergedEmpty(merged)) {
                openDetailForDay(d);
                return;
              }
              // Día vacío: sólo permitimos crear si es hoy o futuro.
              if (d.isBefore(ahoraChile().startOf("day"), "day")) {
                return;
              }
              openCreateBlock(iso);
            }}
            // Guard: sólo actualizamos `month` si realmente cambió el
            // mes panel. Sin este check, AntD disparaba `onPanelChange`
            // en ciertos re-renders (p.ej. tras un refetch que toca
            // `bookings`/`blocks` y por ende la data del Calendar), y
            // el setMonth re-sincronizaba al mes del último click, lo
            // que en producción se veía como "al crear un bloqueo el
            // calendario salta de mes" (si el día clickeado cayó en
            // una celda de borde, AntD puede reportar `d` como del mes
            // adyacente). Comparando `isSame(..., "month")` evitamos
            // tanto el salto como el reset de scroll derivado.
            onPanelChange={(d) => {
              const newMonthStart = d.startOf("month");
              if (!newMonthStart.isSame(month, "month")) {
                setMonth(newMonthStart);
              }
            }}
            /*
             * Render por celda — extraído a CalendarDayCell (componente).
             * Pasamos toda la data ya calculada (allSpans, weekLaneCache,
             * nightPrices, color, propiedad). Round 2026-04-27 (Fase 8).
             */
            dateCellRender={(value) => (
              <CalendarDayCell
                value={value}
                selectedPropertyId={selectedPropertyId}
                selectedPropertyColor={selectedPropertyColor}
                mergedByDate={mergedByDate}
                nightPrices={nightPrices ?? null}
                allSpans={allSpans}
                weekLaneCache={weekLaneCache}
              />
            )}
          />
          </div>

          <BookingDetailModal
            open={isDetailOpen}
            cell={selectedCell}
            propiedades={properties}
            onEditEvent={onEditEvent}
            onDeleteEvent={(ev) => onDeleteEvent(ev)}
            canEditDelete={canEditDelete}
            onClose={() => {
              setIsDetailOpen(false);
              setSelectedCell(null);
            }}
            hideCleaningDetails={true}
            hideInternalNotes={true}
            hideCopyActions={true}
            hideFinancesAction={true}
            hideGuestPhone={true}
            canCreateBlockOnDay={
              selectedCell ? canStartClientBlockOn(selectedCell.date) : false
            }
            onCreateBlockFromDay={(iso) => {
              setIsDetailOpen(false);
              setSelectedCell(null);
              openCreateBlock(iso);
            }}
          />

          <ClientBlockModal
            open={isBlockOpen}
            mode={blockMode}
            propertyId={selectedPropertyId}
            propertyName={
              properties.find((p) => p.id === selectedPropertyId)?.nombre ??
              null
            }
            initial={blockInitial}
            onClose={() => {
              setIsBlockOpen(false);
              setBlockInitial(null);
              setBlockMode("create");
            }}
            onSubmit={onSubmitBlock}
            onDelete={blockMode === "edit" ? onDeleteBlockFromModal : undefined}
            validateNoOverlap={(s, e) =>
              validateNoOverlap(
                s,
                e,
                blockMode === "edit" ? blockInitial?.id : undefined,
              )
            }
            isDayFullyOccupied={(iso) =>
              isDayFullyOccupiedForClient(
                iso,
                blockMode === "edit" ? blockInitial?.id : undefined,
              )
            }
          />
        </div>
      </ConfigProvider>
    </Layout>
  );
}
